/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { useWorkflowCreation } from '../../../hooks/useWorkflowCreation'
import { Formik, FieldArray, yupToFormErrors, setNestedObjectValues } from 'formik'
import { useServiceContext } from '../../../root'
import { WorkflowType } from '../../../constants/workflow'
import UserGroupSteps from '../../../components/workflow/UserGroupSteps'
import Field from '../../..//components/shared/Field'
import ValidationSchema from './FormValidation'
import { ErrorMessageStyled } from '../../..//components/shared/ErrorMessage'
import { Select } from '../../../components/Select/Select'
import Utils from '../../..//lib/Utils'
import Modal from '../../..//components/Modal'
import AddCustomJustificationModal from '../../..//components/modals/AddCustomJustificationModal'
import moment from 'moment'
import { DatePicker, TextArea } from '@mdsol/lego'
import { useEsignature } from '~/hooks/useEsignature'
import { FormGroup } from 'react-bootstrap'
import useWorkflowServiceNew from '~/services/workflow/useWorkflowServiceNew'

interface PageProps {
  fileId?: number
  params?: any
  onClose?: () => void
  title?: string
  initValues?: any
}

const Page: React.FC<PageProps> = ({ fileId, params, onClose, title, initValues }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [isOpen, setIsOpen] = useState(true)
  const [request, setRequest] = useState<any>({
    type: WorkflowType.CHANGE,
    fileId: fileId,
    fileIds: [params?.file?.id ? params.file.id : fileId],
    parentId: 0,
    targetFsiId: fileId,
    obsoleteSourceDocumentUponApproval: params?.obsolete,
    prRemediationChangeReq: initValues?.isPeriodicReview
  })

  const esignature = useEsignature()
  // Create esignatureHandler wrapper for autorunTaskApprovals
  const esignatureHandler = async (options: {
    esignMsg: string
    taskId?: string | number
    workflowStepType?: string
  }) => {
    return esignature.signAction('APPROVE', 'APPROVE_TASK', {
      esignMsg: options.esignMsg
    })
  }
  // Use actual workflow creation hook
  const { createCr } = useWorkflowServiceNew()
  const [availableJustifications1, setAvailableJustifications1] = useState<any[]>([])
  const [showAddCustomModal, setShowAddCustomModal] = useState(false)
  const validationSchema = ValidationSchema()
  let availableJustifications: any[] = []
  const isFileToEtmfOrReclassifyAndSourceFileOwner =
    (params?.reclassify || params?.fileToEtmf) && params?.file && params?.file?.ownerId

  console.log('isFileToEtmfOrReclassifyAndSourceFileOwner', isFileToEtmfOrReclassifyAndSourceFileOwner)
  const isMultiple = () => params?.selectedIds && params?.selectedIds.length > 1
  const doClose = () => {
    setIsOpen(false)
    if (onClose) onClose()
  }

  useEffect(() => {
    // Pass a dummy argument if required by getJustifications
    context.api.settings.getJustifications('').then((data: any) => {
      availableJustifications = data.elements
      availableJustifications.push({ id: 0, text: translate('CUSTOM...') })
      setAvailableJustifications1(Utils.transformToSelectOptions(availableJustifications, 'id', 'text'))
    })
  }, [])

  function assembleData(): Promise<any> {
    return new Promise((resolve) => {
      let reqData: any = {}
      if (params?.reclassify) {
        reqData = {
          ...request,
          type: WorkflowType.RECLASSIFY_DOCUMENT,
          targetFsiId: fileId,
          fileIds: [params?.file.id]
        }
        setRequest(reqData)
      } else if (params?.fileToEtmf) {
        reqData = {
          ...request,
          type: WorkflowType.FILE_ARTIFACT,
          targetFsiId: fileId,
          fileIds: [params?.file.id],
          obsoleteSourceDocumentUponApproval: params?.obsolete
        }
        setRequest(reqData)
        if (isMultiple()) {
          const sourceFilesTemp: any[] = []
          params?.selectedIds?.forEach((id: any) => {
            sourceFilesTemp.push({
              fsiId: id,
              title: request.fileName,
              expirationDate:
                request.expirationDate && request.expirationDate instanceof Date
                  ? request.expirationDate.getTime()
                  : null
            })
          })
          reqData = {
            ...request,
            fileIds: null,
            fileId: null,
            sourceFiles: sourceFilesTemp
          }
          setRequest(reqData)
        }
      }
      resolve(reqData)
    })
  }

  const save = async (values: any, propertiesValues?: any, rawMetadata?: any) => {
    const modifiedValues = { ...values }
    // Merge types as in legacy
    // let type = _.extend({}, modifiedValues.crApprovers, modifiedValues.editors, modifiedValues.approvers, modifiedValues.reviewers)
    const type = {
      ...modifiedValues.crApprovers,
      ...modifiedValues.editors,
      ...modifiedValues.approvers,
      ...modifiedValues.reviewers
    }
    if (modifiedValues.expirationDate && moment.isMoment(modifiedValues.expirationDate)) {
      modifiedValues.expirationDate = modifiedValues.expirationDate.toDate()
    }
    if (request.justificationTextId) {
      modifiedValues.justificationTextId = { value: request.justificationTextId?.value }
    }
    return await assembleData().then((reqData) => {
      const reqLocal = Object.keys(reqData)?.length ? reqData : request
      const apiCall = createCr({
        req: { ...reqLocal, ...modifiedValues },
        type,
        fileToUpload: params?.fileToUpload,
        propertiesValues,
        rawMetadata
      })
      apiCall.then(() => {
        doClose()
      })
      return apiCall
    })
  }

  const validateForm = async (values: any) => {
    try {
      const formRef = context.store.formRef
      if (formRef && typeof formRef.validateForm === 'function') {
        const errors = await formRef.validateForm()
        if (errors && Object.keys(errors).length === 0) {
          save(values)
        } else if (typeof formRef.setTouched === 'function') {
          formRef.setTouched(setNestedObjectValues(errors, true))
        }
      }
    } catch (e) {
      context.service.toast.error(translate('SOMETHING_WENT_WRONG_MSG_ERROR'))
    }
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate(title ? title : '')}
      size="lg"
      className="add-doc-modal-wrapper theme-sandman"
    >
      <Formik
        initialValues={{
          crApprovers: {},
          editors: {},
          reviewers: {},
          approvers: {},
          ...initValues
        }}
        validate={(values) =>
          validationSchema.validate(values, { abortEarly: false, context: values }).then(
            () => ({}),
            (err: unknown) => yupToFormErrors(err)
          )
        }
        onSubmit={(values) => validateForm(values)}
        innerRef={(el: any) => {
          context.store.formRef = el
        }}
        enableReinitialize={true}
      >
        {({ values, setFieldValue, errors, touched, setFieldTouched, handleSubmit }) => (
          <>
            <Modal.Body>
              <div className="form-group w50p">
                <label>{translate('DOCUMENT')}</label>
                <p>{values?.fileToShow?.name}</p>
              </div>
              <div className="form-group">
                <label>{translate('DESCRIPTION')}</label>
                <div className="w50p">
                  <TextArea
                    maxLength={255}
                    name="description"
                    onChange={(e: any) => {
                      setFieldValue('description', e.target.value)
                    }}
                    id="4dc3b5fd-9a20-4325-8931-b2769bf9931a"
                    value={values?.description}
                    rows={5}
                    formatCounter={(length: any, max: any) =>
                      Utils.formatTextAreaCounter(length, max, translate('CHAR_COUNT_LIMIT'))
                    }
                  />
                  <ErrorMessageStyled name="description" />
                </div>
              </div>

              {values?.fileToShow?.type === 'PLACEHOLDER' && !values.docType?.autonamingEnabled && (
                <div className="form-group w50p">
                  <label>
                    {translate('FILENAME_WITHOUT_EXT_LABEL')}{' '}
                    <span className="required">{translate('(OPTIONAL)')}</span>
                  </label>
                  <Field type="text" name="fileName" maxLength={234} id="fileName-field" style={{ width: '100%' }} />
                  <ErrorMessageStyled name="fileName" />
                </div>
              )}

              {values?.fileToShow?.type === 'PLACEHOLDER' && values.docType?.autonamingEnabled && (
                <div className="form-group w50p">
                  <label>{translate('DOCUMENT_TITLE')}</label>
                  <Field
                    type="text"
                    name="fileName"
                    maxLength={255}
                    autoFocus
                    id="fileName-autoname-field"
                    style={{ width: '100%' }}
                  />
                  <ErrorMessageStyled name="fileName" />
                </div>
              )}

              {values?.docType?.requireJustification && (
                <div className="form-group w50p">
                  <label>{translate('JUSTIFICATION')}</label>
                  <Select
                    placeholder={translate('SELECT_JUSTIFICATION_LABEL')}
                    name="justificationTextId"
                    value={values?.justificationTextId}
                    options={availableJustifications1}
                    isClearable={false}
                    onChange={(option: any) => {
                      setFieldValue('justificationTextId', option ? option.value : null)
                      setRequest((prev: any) => ({ ...prev, justificationTextId: option }))
                    }}
                    showNewOptionAtTop={true}
                    id="justification-select"
                  />
                  <ErrorMessageStyled name="justificationTextId" />
                  {values?.justificationTextId === 0 && (
                    <button type="button" className="btn btn-secondary" onClick={() => setShowAddCustomModal(true)}>
                      {translate('DESCRIPTION')}
                    </button>
                  )}
                </div>
              )}

              {values?.docType?.requireExpirationDate && (
                <div className="form-group w50p">
                  <label>{translate('BROWSER_ADD_DOCUMENT_EXPIRATION_DATE_LABEL')}</label>
                  <FormGroup>
                    <DatePicker
                      placeholderText={translate('-SELECT_DATE-')}
                      name="expirationDate"
                      onChange={(val: any) => {
                        setFieldValue('expirationDate', val)
                      }}
                      minDate={moment(new Date()).utc()}
                      selected={values?.expirationDate}
                      popoverTargetOffset="3px 0"
                      todayButton="Today"
                      dateFormatCalendar="MMMM"
                      dateFormat="MMM DD, YYYY"
                      showYearDropdown
                      className="form-control field-control w100p"
                      id="c38252a7-2f13-4d49-996a-dd48ec3d9288"
                    />
                    <ErrorMessageStyled name="expirationDate" />
                  </FormGroup>
                </div>
              )}

              <div className="form-group">
                {values.crApprovers &&
                  values.crApprovers.initialApprovers?.type === 'SETUP_WITH_WORKFLOW' &&
                  !params?.reclassify &&
                  !params?.fileToEtmf && (
                    <FieldArray name="crApprovers.initialApprovers.steps">
                      {(arrayHelpers) => (
                        <UserGroupSteps
                          arrayHelpers={arrayHelpers}
                          values={values.crApprovers?.initialApprovers?.steps || []}
                          nofallTxt={translate('BROWSER_FILE_SETTINGS_CR_APPROVERS_INFO_LABEL')}
                          labelTxt={translate('BROWSER_FILE_SETTINGS_CR_APPROVERS_APPROVAL_SEQUENCE_HEADLINE')}
                          increaseUserGroupWidth={true}
                        />
                      )}
                    </FieldArray>
                  )}

                {values.editors && values.editors.collaborators?.type === 'SETUP_WITH_WORKFLOW' && (
                  <FieldArray name="editors.collaborators.steps">
                    {(arrayHelpers) => (
                      <UserGroupSteps
                        arrayHelpers={arrayHelpers}
                        values={values.editors?.collaborators?.steps || []}
                        nofallTxt={translate('BROWSER_FILE_SETTINGS_EDITORS_INFO_LABEL')}
                        labelTxt={translate('BROWSER_FILE_SETTINGS_EDITORS_SEQUENCE_HEADLINE')}
                        autoAddUser={isFileToEtmfOrReclassifyAndSourceFileOwner ? params?.file?.ownerId : false}
                        increaseUserGroupWidth={true}
                      />
                    )}
                  </FieldArray>
                )}

                {values.reviewers && values.reviewers.reviewers?.type === 'SETUP_WITH_WORKFLOW' && (
                  <FieldArray name="reviewers.reviewers.steps">
                    {(arrayHelpers) => (
                      <UserGroupSteps
                        arrayHelpers={arrayHelpers}
                        values={values.reviewers?.reviewers?.steps || []}
                        nofallTxt={translate('BROWSER_FILE_SETTINGS_REVIEWERS_INFO_LABEL')}
                        labelTxt={translate('BROWSER_FILE_SETTINGS_REVIEWERS_REVIEW_SEQUENCE_HEADLINE')}
                        increaseUserGroupWidth={true}
                      />
                    )}
                  </FieldArray>
                )}

                {values.approvers && values.approvers.approvers?.type === 'SETUP_WITH_WORKFLOW' && (
                  <FieldArray name="approvers.approvers.steps">
                    {(arrayHelpers) => (
                      <UserGroupSteps
                        arrayHelpers={arrayHelpers}
                        values={values.approvers?.approvers?.steps || []}
                        nofallTxt={translate('BROWSER_FILE_SETTINGS_APPROVERS_INFO_LABEL')}
                        labelTxt={translate('BROWSER_FILE_SETTINGS_APPROVERS_APPROVAL_SEQUENCE_HEADLINE')}
                        increaseUserGroupWidth={true}
                      />
                    )}
                  </FieldArray>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button data-action="cancel" className="btn btn-link" onClick={doClose} id="cancel-btn" type="button">
                {translate('CANCEL')}
              </button>
              <button
                data-action="save"
                className="btn btn-primary text-capitalize"
                onClick={() => handleSubmit()}
                id="save-btn"
                type="button"
              >
                {translate('BROWSER_SEND_REQUEST')}
              </button>
              {showAddCustomModal && (
                <AddCustomJustificationModal
                  request={request}
                  setRequest={setRequest}
                  setShowAddCustomModal={setShowAddCustomModal}
                />
              )}
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default Page
